import { useState, useEffect, useCallback } from "react";
import { acquisitionsApi, type Fund, type BudgetSummary } from "@/lib/api/acquisitions";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";

const currentYear = new Date().getFullYear();

export type FundWarningSnapshot = {
  threshold: number | null;
  funds: Array<{
    id: string;
    name: string;
    code: string;
    fiscalYear: number;
    utilizationPercent: number;
  }>;
};

export type TransferFormState = {
  fromFundId: string;
  toFundId: string;
  amount: string;
  reason: string;
};

const EMPTY_TRANSFER: TransferFormState = {
  fromFundId: "",
  toFundId: "",
  amount: "",
  reason: "",
};

export function useBudgetOverview() {
  const toast = useToast();
  const [funds, setFunds] = useState<Fund[]>([]);
  const [summary, setSummary] = useState<BudgetSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fiscalYear, setFiscalYear] = useState<string>(String(currentYear));
  const [activeFilter, setActiveFilter] = useState<string>("active");

  const [transferOpen, setTransferOpen] = useState(false);
  const [transferData, setTransferData] = useState<TransferFormState>(EMPTY_TRANSFER);
  const [transferring, setTransferring] = useState(false);

  const [thresholdInput, setThresholdInput] = useState("");
  const [fundWarnings, setFundWarnings] = useState<FundWarningSnapshot | null>(null);
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [autoApproveInput, setAutoApproveInput] = useState("");
  const [savingAutoApprove, setSavingAutoApprove] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const fy = fiscalYear !== "all" ? parseInt(fiscalYear, 10) : undefined;
      const [fundsRes, summaryRes] = await Promise.all([
        acquisitionsApi.funds.list({
          search: search || undefined,
          isActive: activeFilter === "all" ? undefined : activeFilter === "active",
          fiscalYear: fy,
          limit: 100,
        }),
        acquisitionsApi.funds.summary(fy),
      ]);
      setFunds(fundsRes.funds);
      setTotal(fundsRes.total);
      setSummary(summaryRes);
    } catch (err) {
      console.error("Failed to load budget data", err);
    } finally {
      setLoading(false);
    }
  }, [search, fiscalYear, activeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadBudgetAlerts = useCallback(async () => {
    try {
      const [s, w] = await Promise.all([
        acquisitionsApi.budgetSettings.get(),
        acquisitionsApi.budgetSettings.fundWarnings(),
      ]);
      setThresholdInput(
        s.fundUtilizationWarningPercent != null ? String(s.fundUtilizationWarningPercent) : ""
      );
      setAutoApproveInput(
        s.acquisitionAutoApproveThreshold != null ? String(s.acquisitionAutoApproveThreshold) : ""
      );
      setFundWarnings(w);
    } catch {
      setFundWarnings(null);
    }
  }, []);

  useEffect(() => {
    loadBudgetAlerts();
  }, [loadBudgetAlerts]);

  const saveThreshold = async () => {
    const n = parseInt(thresholdInput, 10);
    if (Number.isNaN(n) || n < 1 || n > 100) {
      toast.error("Enter a whole number between 1 and 100.");
      return;
    }
    setSavingThreshold(true);
    try {
      await acquisitionsApi.budgetSettings.patch({ fundUtilizationWarningPercent: n });
      toast.success("Utilization alert threshold saved.");
      await loadBudgetAlerts();
      await fetchData();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Could not save."));
    } finally {
      setSavingThreshold(false);
    }
  };

  const clearThreshold = async () => {
    setSavingThreshold(true);
    try {
      await acquisitionsApi.budgetSettings.patch({ disableFundUtilizationWarning: true });
      toast.success("Utilization alerts disabled.");
      setThresholdInput("");
      await loadBudgetAlerts();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Could not update."));
    } finally {
      setSavingThreshold(false);
    }
  };

  const saveAutoApprove = async () => {
    const n = parseFloat(autoApproveInput);
    if (Number.isNaN(n) || n <= 0) {
      toast.error("Enter a positive amount (same currency as requests), or disable auto-approval.");
      return;
    }
    setSavingAutoApprove(true);
    try {
      await acquisitionsApi.budgetSettings.patch({ acquisitionAutoApproveThreshold: n });
      toast.success("Auto-approval threshold saved.");
      await loadBudgetAlerts();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Could not save."));
    } finally {
      setSavingAutoApprove(false);
    }
  };

  const clearAutoApprove = async () => {
    setSavingAutoApprove(true);
    try {
      await acquisitionsApi.budgetSettings.patch({ disableAutoApproval: true });
      toast.success("Auto-approval disabled.");
      setAutoApproveInput("");
      await loadBudgetAlerts();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Could not update."));
    } finally {
      setSavingAutoApprove(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferData.fromFundId || !transferData.toFundId || !transferData.amount) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (transferData.fromFundId === transferData.toFundId) {
      toast.error("Source and destination funds must be different");
      return;
    }
    setTransferring(true);
    try {
      await acquisitionsApi.funds.transfer({
        fromFundId: transferData.fromFundId,
        toFundId: transferData.toFundId,
        amount: parseFloat(transferData.amount),
        reason: transferData.reason || undefined,
      });
      toast.success("Transfer completed.");
      setTransferOpen(false);
      setTransferData(EMPTY_TRANSFER);
      await fetchData();
      await loadBudgetAlerts();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Transfer failed."));
    } finally {
      setTransferring(false);
    }
  };

  return {
    funds,
    summary,
    total,
    loading,
    search,
    setSearch,
    fiscalYear,
    setFiscalYear,
    activeFilter,
    setActiveFilter,
    transferOpen,
    setTransferOpen,
    transferData,
    setTransferData,
    transferring,
    handleTransfer,
    thresholdInput,
    setThresholdInput,
    fundWarnings,
    savingThreshold,
    saveThreshold,
    clearThreshold,
    autoApproveInput,
    setAutoApproveInput,
    savingAutoApprove,
    saveAutoApprove,
    clearAutoApprove,
    yearOptions: Array.from({ length: 5 }, (_, i) => currentYear - 2 + i),
  };
}
